import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/config/api";
import { showError } from "@/utils/dialog";

export default function TeamMemberProgress() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    range: "day",
    from: "",
    to: "",
  });
  const [taskQuery, setTaskQuery] = useState("");
  const [ticketQuery, setTicketQuery] = useState("");
  const [activityQuery, setActivityQuery] = useState("");
  const [activityType, setActivityType] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.users.getProgress(id, filters);
      setData(res || null);
    } catch (error) {
      await showError(error.message || "Failed to load team progress");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const member = data?.member;
  const summary = data?.summary || {};
  const rangeLabel =
    filters.range === "week"
      ? "Last 7 days"
      : filters.range === "month"
        ? "Last 30 days"
        : filters.range === "custom"
          ? "Custom range"
          : "Today";
  const tasks = useMemo(() => data?.tasks || [], [data?.tasks]);
  const tickets = useMemo(() => data?.tickets || [], [data?.tickets]);
  const activities = useMemo(() => data?.activities || [], [data?.activities]);
  const filteredTasks = useMemo(() => {
    const q = taskQuery.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter(
      (t) =>
        String(t.title || "").toLowerCase().includes(q) ||
        String(t.description || "").toLowerCase().includes(q),
    );
  }, [tasks, taskQuery]);
  const filteredTickets = useMemo(() => {
    const q = ticketQuery.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter(
      (t) =>
        String(t.title || "").toLowerCase().includes(q) ||
        String(t.status || "").toLowerCase().includes(q),
    );
  }, [tickets, ticketQuery]);
  const filteredActivities = useMemo(() => {
    const q = activityQuery.trim().toLowerCase();
    const byType = activities.filter((a) => {
      if (activityType === "all") return true;
      if (activityType === "ticket") {
        return (
          String(a.entity_type || "").toLowerCase() === "workflow_ticket" ||
          String(a.action || "").toLowerCase().includes("workflow_ticket")
        );
      }
      return String(a.entity_type || "").toLowerCase() === activityType;
    });
    if (!q) return byType;
    return byType.filter(
      (a) =>
        String(a.action || "").toLowerCase().includes(q) ||
        String(a.entity_type || "").toLowerCase().includes(q),
    );
  }, [activities, activityQuery, activityType]);

  if (loading) {
    return <div className="text-sm text-stone-500">Loading member progress...</div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-secondary leading-tight">
            Team Member Progress
          </h1>
          <p className="text-sm text-stone-600 mt-1 max-w-2xl">
            Finance-style progress view for member productivity, task completion, and workflow execution.
          </p>
        </div>
        <Link
          to="/team"
          className="px-3 py-2 border border-stone-300 rounded-lg text-sm hover:bg-stone-50 shadow-sm"
        >
          Back to Team
        </Link>
      </div>

      {member && (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="px-4 py-2 bg-stone-50 border-b border-stone-200">
            <p className="text-xs font-semibold tracking-wide uppercase text-stone-600">Team Member Profile</p>
          </div>
          <div className="p-4">
            <p className="font-semibold text-stone-800 text-lg">{member.name}</p>
            <p className="text-sm text-stone-600 mt-1">{member.email}</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="px-2.5 py-1 text-xs rounded-full bg-cyan-100 text-cyan-700 font-medium">
                Role: {member.role}
              </span>
              <span className={`px-2.5 py-1 text-xs rounded-full font-medium ${member.is_active ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {member.is_active ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="px-4 py-2 bg-stone-50 border-b border-stone-200">
          <p className="text-xs font-semibold tracking-wide uppercase text-stone-600">Progress Filters</p>
        </div>
        <div className="p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={filters.range}
            onChange={(e) => setFilters((p) => ({ ...p, range: e.target.value }))}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
          >
            <option value="day">Today</option>
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="custom">Custom range</option>
          </select>
          {filters.range === "custom" && (
            <>
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
              />
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={load}
                className="px-3 py-2 bg-primary text-white rounded-lg text-sm"
              >
                Apply
              </button>
            </>
          )}
        </div>
        <p className="text-xs text-stone-500 mt-2">
          Helper: filter by date range to audit team productivity and back-date execution history.
        </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <p className="text-xs text-stone-500">Tasks total</p>
          <p className="text-2xl font-semibold text-stone-800">{summary.tasks_total || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <p className="text-xs text-stone-500">Tasks completed</p>
          <p className="text-2xl font-semibold text-emerald-700">{summary.tasks_completed || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <p className="text-xs text-stone-500">Tickets total</p>
          <p className="text-2xl font-semibold text-stone-800">{summary.tickets_total || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <p className="text-xs text-stone-500">Tickets completed</p>
          <p className="text-2xl font-semibold text-blue-700">{summary.tickets_completed || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <p className="text-xs text-stone-500">Actions logged</p>
          <p className="text-2xl font-semibold text-stone-800">{summary.actions_total || 0}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 px-4 py-3">
        <p className="text-sm text-stone-700">
          Showing <span className="font-semibold">{rangeLabel}</span> window from{" "}
          <span className="font-semibold">{data?.range?.from || "-"}</span> to{" "}
          <span className="font-semibold">{data?.range?.to || "-"}</span>.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="bg-white rounded-xl border border-stone-200 overflow-hidden lg:col-span-1">
          <div className="px-4 py-2 bg-stone-50 border-b border-stone-200">
            <h2 className="font-semibold text-stone-800">Tasks</h2>
          </div>
          <div className="p-4">
          <input
            value={taskQuery}
            onChange={(e) => setTaskQuery(e.target.value)}
            placeholder="Search task title..."
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm mb-2"
          />
          <p className="text-xs text-stone-500 mb-2">
            Showing {filteredTasks.length} of {tasks.length} tasks
          </p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {filteredTasks.map((task) => (
              <div key={task.id} className="border border-stone-200 rounded-lg p-2">
                <p className="text-sm font-medium text-stone-800">{task.title}</p>
                <p className="text-xs text-stone-600">{task.status} • {String(task.task_date).slice(0, 10)}</p>
              </div>
            ))}
            {!filteredTasks.length && <p className="text-sm text-stone-500">No tasks in this range.</p>}
          </div>
          </div>
        </section>
        <section className="bg-white rounded-xl border border-stone-200 overflow-hidden lg:col-span-1">
          <div className="px-4 py-2 bg-stone-50 border-b border-stone-200">
            <h2 className="font-semibold text-stone-800">Tickets</h2>
          </div>
          <div className="p-4">
          <input
            value={ticketQuery}
            onChange={(e) => setTicketQuery(e.target.value)}
            placeholder="Search ticket title/status..."
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm mb-2"
          />
          <p className="text-xs text-stone-500 mb-2">
            Showing {filteredTickets.length} of {tickets.length} tickets
          </p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {filteredTickets.map((ticket) => (
              <div key={ticket.id} className="border border-stone-200 rounded-lg p-2">
                <p className="text-sm font-medium text-stone-800">{ticket.title}</p>
                <p className="text-xs text-stone-600">{ticket.status} • {String(ticket.created_at).slice(0, 10)}</p>
              </div>
            ))}
            {!filteredTickets.length && <p className="text-sm text-stone-500">No tickets in this range.</p>}
          </div>
          </div>
        </section>
        <section className="bg-white rounded-xl border border-stone-200 overflow-hidden lg:col-span-1">
          <div className="px-4 py-2 bg-stone-50 border-b border-stone-200">
            <h2 className="font-semibold text-stone-800">Audit Activities</h2>
          </div>
          <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <select
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
              className="px-3 py-2 border border-stone-300 rounded-lg text-sm"
            >
              <option value="all">All activity types</option>
              <option value="ticket">Ticket actions</option>
              <option value="customer">Customer actions</option>
              <option value="vendor">Vendor actions</option>
              <option value="invoice">Invoice actions</option>
              <option value="payment">Payment actions</option>
              <option value="user">User/team actions</option>
              <option value="settings">Settings actions</option>
            </select>
          </div>
          <input
            value={activityQuery}
            onChange={(e) => setActivityQuery(e.target.value)}
            placeholder="Search action/entity..."
            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm mb-2"
          />
          <p className="text-xs text-stone-500 mb-2">
            Showing {filteredActivities.length} of {activities.length} activities
          </p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {filteredActivities.map((log) => (
              <div key={log.id} className="border border-stone-200 rounded-lg p-2">
                <p className="text-sm font-medium text-stone-800">{log.action}</p>
                <p className="text-xs text-stone-600">{log.entity_type} • {String(log.created_at).slice(0, 10)}</p>
              </div>
            ))}
            {!filteredActivities.length && <p className="text-sm text-stone-500">No activity in this range.</p>}
          </div>
          </div>
        </section>
      </div>
    </div>
  );
}
